import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import UserLayout from "../../components/UserLayout";
import { api } from "../../utils/api";
import { useToast } from "../../contexts/ToastContext";
import DataTemplateModal from "../../components/modals/DataTemplateModal.jsx";
import { useAuth } from "../../contexts/AuthContext";

export default function Dashboard() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTemplates, setShowTemplates] = useState(false);
  const [applications, setApplications] = useState([]);

  useEffect(() => {
    fetchDashboard();
    fetchApplications();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await api.getDashboard();
      setData(res.data.data);
    } catch {
      showToast("Failed to load dashboard", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchApplications = async () => {
    try {
      const res = await api.getApplications();
      setApplications(res.data);
    } catch {
      showToast("Failed to load applications", "error");
    }
  };

  const goToApplications = (statusKey) => {
    navigate(`/applications?status=${statusKey}`);
  };

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      : "-";

  const getStatusBadge = (status) => {
    const statusConfig = {
      "Application Received, Pending for Review": {
        color: "bg-amber-50 text-amber-700",
        icon: "⏳",
      },
      "Additional Information Requested": {
        color: "bg-orange-50 text-orange-700",
        icon: "📝",
      },
      "Additional Information Received": {
        color: "bg-indigo-50 text-indigo-700",
        icon: "✓",
      },
      "Review Passed - Pending for Submission": {
        color: "bg-blue-50 text-blue-700",
        icon: "📋",
      },
      "Submission Done - Awaiting Visa Status": {
        color: "bg-purple-50 text-purple-700",
        icon: "⏱️",
      },
      "Visa Approved": {
        color: "bg-emerald-50 text-emerald-700",
        icon: "✅",
      },
      "Visa Rejected": {
        color: "bg-red-50 text-red-700",
        icon: "❌",
      },
      draft: {
        color: "bg-slate-50 text-slate-600",
        icon: "📄",
      },
    };

    const config = statusConfig[status] || statusConfig.draft;

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-md ${config.color}`}
      >
        <span>{config.icon}</span>
        {status}
      </span>
    );
  };

  const handleVisaDownload = async (applicationId) => {
    try {
      const response = await api.downloadVisa(applicationId);

      if (response.data && response.data.download_url) {
        const fileResponse = await fetch(response.data.download_url);
        const blob = await fileResponse.blob();

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          response.data.file_name || `visa_${applicationId}.pdf`,
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        showToast("Visa document downloaded successfully", "success");
      }
    } catch (error) {
      console.error("Visa download failed:", error);
      showToast("Failed to download visa document", "error");
    }
  };

  if (loading) {
    return (
      <UserLayout>
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="text-center">
            <div className="animate-spin h-8 w-8 border-3 border-blue-600 border-t-transparent rounded-full mx-auto mb-3"></div>
            <p className="text-sm text-slate-600 font-medium">
              Loading dashboard...
            </p>
          </div>
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* HEADER */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl font-bold text-slate-900">Dashboard</h1>
            </div>
            <button
              onClick={() => navigate("/create-new-application")}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg shadow-sm hover:shadow transition-all duration-200"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              New Application
            </button>
          </div>
        </div>

        {/* METRIC CARDS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
          <StatCard
            title="Total Applications"
            value={data?.stats?.total || 0}
            icon="📊"
            colorClass="bg-white hover:bg-blue-50 border-slate-200"
            textColor="text-slate-900"
            onClick={() => goToApplications("all")}
          />

          <StatCard
            title="Under Review"
            value={data?.stats?.pending || 0}
            icon="⏳"
            colorClass="bg-white hover:bg-amber-50 border-slate-200"
            textColor="text-slate-900"
            onClick={() => goToApplications("under_review")}
          />

          <StatCard
            title="Additional Info"
            value={data?.stats?.additional_info || 0}
            icon="📝"
            colorClass="bg-white hover:bg-orange-50 border-slate-200"
            textColor="text-slate-900"
            onClick={() => goToApplications("additional_info")}
          />

          <StatCard
            title="Approved"
            value={data?.stats?.approved || 0}
            icon="✅"
            colorClass="bg-white hover:bg-emerald-50 border-slate-200"
            textColor="text-slate-900"
            onClick={() => goToApplications("approved")}
          />

          <StatCard
            title="Rejected"
            value={data?.stats?.rejected || 0}
            icon="❌"
            colorClass="bg-white hover:bg-red-50 border-slate-200"
            textColor="text-slate-900"
            onClick={() => goToApplications("rejected")}
          />
        </div>

        {/* TEMPLATE DOWNLOAD CARD */}
        <div className="bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg p-4 mb-6 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center text-xl flex-shrink-0">
                📥
              </div>
              <div>
                <h3 className="text-base font-semibold text-white">
                  Download DU Data Templates
                </h3>
                <p className="text-blue-100 text-xs mt-0.5">
                  Tourist & Business visa templates
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowTemplates(true)}
              className="px-4 py-2 bg-white hover:bg-blue-50 text-blue-600 text-sm font-medium rounded-lg shadow-sm transition-all duration-200 whitespace-nowrap"
            >
              Download Templates
            </button>
          </div>
        </div>

        {/* RECENT APPLICATIONS */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 bg-slate-50/50">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-900">
                Recent Applications
              </h3>
            </div>
          </div>

          {applications.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Application ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Applicant
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Visa Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {applications.slice(0, 5).map((app) => (
                    <tr
                      key={app.id}
                      className="hover:bg-slate-50 transition-colors duration-150"
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600">
                        {formatDate(app.created_at)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm font-mono text-slate-700">
                          {app.id}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="text-sm font-medium text-slate-900">
                          {app.applicant_name || "N/A"}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-slate-900">
                          {app.visa_category}
                        </div>
                        <div className="text-xs text-slate-500">
                          {app.visa_type_name}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {getStatusBadge(app.status)}
                      </td>
                      {/* In the table body, replace the actions cell */}


                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <div className="inline-flex items-center gap-1.5">

                          {/* VIEW ICON - Always visible for ALL applications */}
                          <button
                            onClick={() => navigate(`/applications/${app.id}`)}
                            className="inline-flex items-center justify-center w-8 h-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
                            title="View Application"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                              />
                            </svg>
                          </button>

                          {/* EDIT ICON - Only visible for DRAFT status */}
                          {app.status === "draft" && (
                            <button
                              onClick={() => navigate(`/applications/${app.id}?mode=edit`)}
                              className="inline-flex items-center justify-center w-8 h-8 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors"
                              title="Edit Application"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                />
                              </svg>
                            </button>
                          )}

                          {/* DOWNLOAD ICON - Only for approved applications */}
                          {app.visa_uploaded === true && (
                            <button
                              onClick={() => handleVisaDownload(app.id)}
                              className="inline-flex items-center justify-center w-8 h-8 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-md transition-colors"
                              title="Download Visa"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                />
                              </svg>
                            </button>
                          )}

                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center">
              <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3 text-2xl">
                📋
              </div>
              <h4 className="text-sm text-slate-900 font-semibold mb-1">
                No applications yet
              </h4>
              <p className="text-xs text-slate-500 mb-4">
                Get started by creating your first application
              </p>
              <button
                onClick={() => navigate("/create-new-application")}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-xs bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg shadow-sm transition-all duration-200"
              >
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Create Application
              </button>
            </div>
          )}
        </div>

        <DataTemplateModal
          open={showTemplates}
          onClose={() => setShowTemplates(false)}
          country={user?.country}
        />
      </div>
    </UserLayout>
  );
}

// Clean StatCard Component
function StatCard({ title, value, icon, colorClass, textColor, onClick }) {
  return (
    <div
      onClick={onClick}
      className={`${colorClass} border rounded-lg p-4 cursor-pointer transition-all duration-200 hover:shadow-md group`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-2xl transform group-hover:scale-110 transition-transform duration-200">
          {icon}
        </div>
        <div className={`text-2xl font-bold ${textColor}`}>{value}</div>
      </div>
      <p className="text-xs font-medium text-slate-600">{title}</p>
    </div>
  );
}
